import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router-dom';

import { getAuthClient } from './utils/auth';
import { buildYourTourApiUrl, fetchYourTourApi, getNormalizedApiDataByUuid } from './utils/ApiHelpers';
import { useAsyncEffect } from './hooks/useAsyncEffect';
import useAppAuth from './hooks/useAppAuth';
import useAppSettings from './hooks/useAppSettings';
import useMapData from './hooks/useMapData';
import useCommunityAccess from './hooks/useCommunityAccess';
import { useAuthStore } from './stores';
import { createSalespersonName } from './utils/DrupalEntityTypes/User/UserHelpers';
import { userNormalizeData } from './utils/DrupalEntityTypes/User/UserNormalizeData';
import CacheBuster from './CacheBuster';
import { FavoriteButtonsProvider } from './Components/Personalization/Favorite/FavoriteButtonsContext';

import AppSwitchboard from './Components/AppSwitchboard/AppSwitchboard';
import './GlobalScss/global.scss';
import './App.scss';

const auth = getAuthClient();

const App = ({ history }) => {
  // Local state for community data
  const [communityData, setCommunityData] = useState(null);

  // Settings hook - handles settings, config, window resize, page title, analytics
  const {
    settings, containerStyle, featureFlags, pathname,
  } = useAppSettings(communityData);

  const {
    platform,
    isPublic,
    urlSlug,
    mode,
  } = settings;

  const { allowLogin } = featureFlags || {};

  // Auth hook - handles all authentication logic
  const { isAuthed, isLoggedIn, isLoggingIn } = useAppAuth({
    platform,
    isPublic,
    allowLogin,
    history,
  });

  // Lightweight refetch used by the heartbeat when the community config
  // version changes — pulls fresh /config/{slug} so feature flags and other
  // community-level settings propagate without a page reload. Access-denial
  // handling lives in the initial useAsyncEffect below; live updates ignore
  // errors and rely on the next heartbeat tick to retry.
  const refetchCommunityData = useCallback(async () => {
    if (!urlSlug || !platform) return;
    try {
      const url = buildYourTourApiUrl(`/your_tour_api/config/${urlSlug}`, {}, platform);
      const rawCommunityData = await fetchYourTourApi(url, platform);
      if (rawCommunityData?.yt?.communityNid) {
        setCommunityData(rawCommunityData);
      }
    } catch (error) {
      // Silent — next heartbeat bump will retry.
    }
  }, [urlSlug, platform]);

  // Map data hook - fetches map data when urlSlug is available
  useMapData(urlSlug, platform, { onCommunityConfigChange: refetchCommunityData, mode });

  // Access control from auth store - used to show error screen when API returns 403
  const denyAccess = useAuthStore((state) => state.denyAccess);

  // User info from auth store - fetch early so it's available for error messages
  const salespersonId = useAuthStore((state) => state.salespersonId);
  const salespersonName = useAuthStore((state) => state.salespersonName);
  const setSalespersonName = useAuthStore((state) => state.setSalespersonName);

  // Fetch user info when logged in - needed for error messages
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const request = {
          entityType: 'user',
          contentType: 'user',
          uuid: salespersonId,
        };
        const data = await getNormalizedApiDataByUuid(request, userNormalizeData);
        if (data) {
          const name = createSalespersonName(data);
          setSalespersonName(name);
        }
      } catch (error) {
        // Silently fail - we'll just not have the username for error messages
        console.log('Could not fetch user info for error messages');
      }
    };

    // Fetch if we have an ID but no name yet
    if (salespersonId && !salespersonName) {
      fetchUserInfo();
    }
  }, [salespersonId, salespersonName, setSalespersonName]);

  // Platforms that are public and should not check access
  const PUBLIC_PLATFORMS = ['page', 'siteSee', 'kiosk', 'mapOnly', 'mapTiles'];
  const isPublicPlatform = PUBLIC_PLATFORMS.includes(platform);

  // Once authed, get and set all config data with proper cleanup
  useAsyncEffect(async (isMounted) => {
    if (!urlSlug || !isAuthed) return;

    try {
      // Use buildYourTourApiUrl to include platform parameter for access control
      // Pass platform directly since app store might not be updated yet
      const url = buildYourTourApiUrl(`/your_tour_api/config/${urlSlug}`, {}, platform);

      const rawCommunityData = await fetchYourTourApi(url, platform);

      if (isMounted()) {
        // Check if we got valid community data (has yt config)
        const hasValidData = rawCommunityData && rawCommunityData.yt && rawCommunityData.yt.communityNid;

        // If we got valid data, use it regardless of other fields in the response
        if (hasValidData) {
          setCommunityData(rawCommunityData);
          return;
        }

        // Check for access denied error from server (403 response)
        const hasAccessError = rawCommunityData?.error === 'access_denied';

        // For logged-in users on non-public platforms: if no valid data, deny access
        if (isLoggedIn && !isPublicPlatform) {
          const message = rawCommunityData?.message || 'You do not have access to this community.';
          denyAccess(message);
          setCommunityData(null);
          return;
        }

        if (hasAccessError) {
          // Not logged in - don't call denyAccess, let auth flow handle redirect to login
          setCommunityData(null);
        }
      }
    } catch (error) {
      if (isMounted()) {
        console.error('Failed to fetch community data:', error);
        // For logged-in YT users, if the request failed completely, deny access
        if (isLoggedIn && platform === 'yt') {
          denyAccess('Unable to verify your access to this community. Please try again or contact support.');
        }
        setCommunityData(null);
      }
    }
  }, [isAuthed, isLoggedIn, isPublicPlatform, platform, urlSlug, denyAccess]);

  // Community access check - only for logged-in users on YT platform
  // Skipped for: page (PIB), siteSee, kiosk, mapOnly, etc.
  // Results are consumed by AppSwitchboard to show error screen
  const communityNid = communityData?.yt?.communityNid || null;
  useCommunityAccess({
    isLoggedIn,
    isPublic,
    platform,
    communityNid,
  });

  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) {
          refreshCacheAndReload();
        }
        if (pathname === '/forward') {
          return null;
        }
        return (
          <FavoriteButtonsProvider>
            <div className="container" style={containerStyle}>
              <AppSwitchboard
                isAuthed={isAuthed}
                isLoggedIn={isLoggedIn}
                isLoggingIn={isLoggingIn}
              />
            </div>
          </FavoriteButtonsProvider>
        );
      }}
    </CacheBuster>
  );
};

export default withRouter(App);
